/**
 * Procedural audio. Every sound is synthesised at runtime from oscillators and
 * a white-noise buffer — the game ships no audio assets, so the bundle stays
 * small and the whole thing keeps working on trade-fair wifi (or none at all).
 *
 * The context is created lazily on the first `unlock()` because iOS refuses to
 * start one outside a user gesture.
 */
import type { GameEvent } from '@/game/types'

export interface AudioEngine {
  readonly muted: boolean
  setMuted(m: boolean): void
  /** Must be called from a real user gesture before any sound (iOS autoplay policy). */
  unlock(): void
  handle(event: GameEvent): void
  /** 0..1, driven by truck speed; shifts music tempo/filter. */
  setIntensity(v: number): void
  startMusic(): void
  stopMusic(): void
  dispose(): void
}

/* ------------------------------------------------------------- tuning ---- */

const MASTER_LEVEL = 0.55
const MUSIC_LEVEL = 0.22
/** Hard cap on simultaneous SFX voices; the oldest is dropped past this. */
const MAX_VOICES = 12
/** Seconds of the mute / unmute gain ramp. */
const MUTE_RAMP = 0.06
/** How far ahead the sequencer schedules, seconds. */
const LOOKAHEAD = 0.2
/** Sequencer wake-up period, ms. */
const TIMER_MS = 50
const BPM_MIN = 104
const BPM_MAX = 148
const STEPS_PER_BAR = 16
/** Smallest audible gain — `exponentialRampToValueAtTime` cannot reach 0. */
const SILENCE = 0.0001

/** Am - F - C - G. `third` is 3 for the minor bar, 4 for the major ones. */
const PROGRESSION: readonly { root: number; third: number }[] = [
  { root: 45, third: 3 },
  { root: 41, third: 4 },
  { root: 48, third: 4 },
  { root: 43, third: 3 },
]

const TOTAL_STEPS = STEPS_PER_BAR * PROGRESSION.length

type LegacyWindow = Window & { webkitAudioContext?: typeof AudioContext }

/** MIDI note number to Hz. */
function hz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

/** One SFX: a bus plus everything hanging off it, torn down when it ends. */
interface Voice {
  bus: GainNode
  sources: AudioScheduledSourceNode[]
  parts: AudioNode[]
  dead: boolean
}

class ProceduralAudio implements AudioEngine {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private limiter: DynamicsCompressorNode | null = null
  private musicBus: GainNode | null = null
  private noise: AudioBuffer | null = null

  private isMuted = false
  private intensity = 0

  private voices: Voice[] = []

  private musicWanted = false
  private timer: number | null = null
  private nextStepTime = 0
  private step = 0

  get muted(): boolean {
    return this.isMuted
  }

  /* ------------------------------------------------------------ lifecycle */

  unlock(): void {
    if (!this.ctx) {
      const Ctor: typeof AudioContext | undefined =
        typeof AudioContext !== 'undefined' ? AudioContext : (
          (window as LegacyWindow).webkitAudioContext
        )
      if (!Ctor) return

      const ctx = new Ctor()
      const limiter = ctx.createDynamicsCompressor()
      limiter.threshold.value = -8
      limiter.knee.value = 6
      limiter.ratio.value = 12
      limiter.attack.value = 0.003
      limiter.release.value = 0.18
      limiter.connect(ctx.destination)

      const master = ctx.createGain()
      master.gain.value = this.isMuted ? 0 : MASTER_LEVEL
      master.connect(limiter)

      const musicBus = ctx.createGain()
      musicBus.gain.value = MUSIC_LEVEL
      musicBus.connect(master)

      this.ctx = ctx
      this.limiter = limiter
      this.master = master
      this.musicBus = musicBus
      this.noise = makeNoise(ctx)
    }

    if (this.ctx.state === 'suspended') void this.ctx.resume()
    if (this.musicWanted) this.runSequencer()
  }

  setMuted(m: boolean): void {
    if (m === this.isMuted) return
    this.isMuted = m

    const ctx = this.ctx
    const master = this.master
    if (ctx && master) {
      const t = ctx.currentTime
      master.gain.cancelScheduledValues(t)
      master.gain.setValueAtTime(master.gain.value, t)
      master.gain.linearRampToValueAtTime(m ? 0 : MASTER_LEVEL, t + MUTE_RAMP)
    }

    if (m) {
      this.stopSequencer()
      for (const v of [...this.voices]) this.kill(v)
    } else if (this.musicWanted) {
      this.runSequencer()
    }
  }

  setIntensity(v: number): void {
    this.intensity =
      v < 0 ? 0
      : v > 1 ? 1
      : v
  }

  dispose(): void {
    this.stopSequencer()
    this.musicWanted = false
    for (const v of [...this.voices]) this.kill(v)
    this.voices = []
    this.musicBus?.disconnect()
    this.master?.disconnect()
    this.limiter?.disconnect()
    const ctx = this.ctx
    this.musicBus = null
    this.master = null
    this.limiter = null
    this.noise = null
    this.ctx = null
    if (ctx && ctx.state !== 'closed') void ctx.close()
  }

  /* ----------------------------------------------------------------- sfx */

  handle(event: GameEvent): void {
    const ctx = this.ctx
    if (!ctx || this.isMuted) return
    const t = ctx.currentTime

    switch (event.kind) {
      case 'lane':
        this.scrub(t)
        break
      case 'order':
        this.blip(t, 880, 0.035, 'square', 0.16)
        this.blip(t + 0.035, 1320, 0.035, 'square', 0.14)
        break
      case 'fuel':
        this.warmBlip(t)
        break
      case 'comboUp':
        this.comboUp(t, event.combo ?? 1)
        break
      case 'comboLost':
        this.comboLost(t)
        break
      case 'bobi':
        this.chord(t, 'bobi')
        break
      case 'sokar':
        this.chord(t, 'sokar')
        break
      case 'autopilotDodge':
        this.blip(t, 2100, 0.035, 'sine', 0.05)
        break
      case 'powerupEnd':
        this.slide(t, 660, 330, 0.14, 'triangle', 0.1)
        break
      case 'nearMiss':
        this.whoosh(t)
        break
      case 'crash':
        this.crash(t)
        break
    }
  }

  /** Tyre scrub: a very short band-passed noise sweep, deliberately quiet. */
  private scrub(t: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const src = this.noiseSource(ctx)
    if (!src) return
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.Q.value = 1.1
    bp.frequency.setValueAtTime(2400, t)
    bp.frequency.exponentialRampToValueAtTime(700, t + 0.09)

    envelope(v.bus.gain, t, 0.006, 0.09, 0.07)
    src.connect(bp).connect(v.bus)
    v.parts.push(bp)
    this.arm(v, src, t, t + 0.1)
  }

  /** Plain single-oscillator blip. */
  private blip(t: number, freq: number, dur: number, type: OscillatorType, peak: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const osc = ctx.createOscillator()
    osc.type = type
    osc.frequency.setValueAtTime(freq, t)

    envelope(v.bus.gain, t, 0.004, dur, peak)
    osc.connect(v.bus)
    this.arm(v, osc, t, t + dur + 0.02)
  }

  /** Blip with a glide between two pitches. */
  private slide(
    t: number,
    from: number,
    to: number,
    dur: number,
    type: OscillatorType,
    peak: number,
  ): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const osc = ctx.createOscillator()
    osc.type = type
    osc.frequency.setValueAtTime(from, t)
    osc.frequency.exponentialRampToValueAtTime(to, t + dur)

    envelope(v.bus.gain, t, 0.005, dur, peak)
    osc.connect(v.bus)
    this.arm(v, osc, t, t + dur + 0.02)
  }

  /** Fuel: warm triangle body with a small pitch rise and a lowpass lid. */
  private warmBlip(t: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const osc = ctx.createOscillator()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(392, t)
    osc.frequency.exponentialRampToValueAtTime(660, t + 0.14)

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 2200

    envelope(v.bus.gain, t, 0.008, 0.18, 0.2)
    osc.connect(lp).connect(v.bus)
    v.parts.push(lp)
    this.arm(v, osc, t, t + 0.2)
  }

  /** Three rising notes; the whole arpeggio is transposed up with the combo. */
  private comboUp(t: number, combo: number): void {
    const base = 523.25 * Math.pow(2, Math.min(Math.max(combo, 1), 8) / 12)
    const offsets = [0, 4, 7]
    for (let i = 0; i < offsets.length; i += 1) {
      const freq = base * Math.pow(2, offsets[i] / 12)
      this.blip(t + i * 0.055, freq, 0.06, 'triangle', 0.17)
    }
  }

  /** Descending minor third, filtered down so it reads as a small loss. */
  private comboLost(t: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const osc = ctx.createOscillator()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(392, t)
    osc.frequency.setValueAtTime(392, t + 0.1)
    osc.frequency.exponentialRampToValueAtTime(329.63, t + 0.12)

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 900

    envelope(v.bus.gain, t, 0.01, 0.22, 0.12)
    osc.connect(lp).connect(v.bus)
    v.parts.push(lp)
    this.arm(v, osc, t, t + 0.24)
  }

  /** Power-up stinger: three notes at once under an opening filter. */
  private chord(t: number, which: 'bobi' | 'sokar'): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const bobi = which === 'bobi'
    const roots = bobi ? [220, 330, 440] : [293.66, 440, 587.33]
    const dur = bobi ? 0.52 : 0.46

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.Q.value = bobi ? 4 : 2
    lp.frequency.setValueAtTime(bobi ? 400 : 1200, t)
    lp.frequency.exponentialRampToValueAtTime(bobi ? 4200 : 6000, t + dur * 0.6)
    lp.connect(v.bus)
    v.parts.push(lp)

    envelope(v.bus.gain, t, 0.012, dur, bobi ? 0.22 : 0.19)

    for (let i = 0; i < roots.length; i += 1) {
      const osc = ctx.createOscillator()
      osc.type = bobi ? 'sawtooth' : 'square'
      osc.frequency.setValueAtTime(roots[i], t)
      osc.detune.value = i === 1 ? 6 : 0
      osc.connect(lp)
      if (i === roots.length - 1) {
        // The top note drives the teardown for the whole voice.
        this.arm(v, osc, t, t + dur + 0.03)
      } else {
        v.sources.push(osc)
        osc.start(t)
        osc.stop(t + dur + 0.03)
      }
    }
  }

  /** Near miss: noise under a fast bandpass sweep. */
  private whoosh(t: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const src = this.noiseSource(ctx)
    if (!src) return
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.Q.value = 1.6
    bp.frequency.setValueAtTime(400, t)
    bp.frequency.exponentialRampToValueAtTime(2600, t + 0.16)
    bp.frequency.exponentialRampToValueAtTime(600, t + 0.26)

    const g = v.bus.gain
    g.setValueAtTime(SILENCE, t)
    g.exponentialRampToValueAtTime(0.14, t + 0.1)
    g.exponentialRampToValueAtTime(SILENCE, t + 0.26)

    src.connect(bp).connect(v.bus)
    v.parts.push(bp)
    this.arm(v, src, t, t + 0.28)
  }

  /** Crash: a noise burst plus a low sine thud. The loudest thing in the game. */
  private crash(t: number): void {
    const ctx = this.ctx
    const v = this.newVoice()
    if (!ctx || !v) return

    const src = this.noiseSource(ctx)
    if (!src) return
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.setValueAtTime(5200, t)
    lp.frequency.exponentialRampToValueAtTime(320, t + 0.5)

    const noiseGain = ctx.createGain()
    envelope(noiseGain.gain, t, 0.002, 0.45, 0.5)

    const thud = ctx.createOscillator()
    thud.type = 'sine'
    thud.frequency.setValueAtTime(140, t)
    thud.frequency.exponentialRampToValueAtTime(42, t + 0.35)

    const thudGain = ctx.createGain()
    envelope(thudGain.gain, t, 0.004, 0.6, 0.6)

    v.bus.gain.value = 0.9
    src.connect(lp).connect(noiseGain).connect(v.bus)
    thud.connect(thudGain).connect(v.bus)
    v.parts.push(lp, noiseGain, thudGain)

    v.sources.push(src)
    src.start(t)
    src.stop(t + 0.5)
    this.arm(v, thud, t, t + 0.65)
  }

  /* --------------------------------------------------------------- music */

  startMusic(): void {
    if (this.musicWanted) return
    this.musicWanted = true
    this.step = 0
    if (!this.isMuted) this.runSequencer()
  }

  stopMusic(): void {
    if (!this.musicWanted) return
    this.musicWanted = false
    this.stopSequencer()
  }

  private runSequencer(): void {
    const ctx = this.ctx
    if (!ctx || this.timer !== null) return
    this.nextStepTime = ctx.currentTime + 0.08
    this.tick()
  }

  private stopSequencer(): void {
    if (this.timer === null) return
    window.clearTimeout(this.timer)
    this.timer = null
  }

  private tick = (): void => {
    this.timer = null
    const ctx = this.ctx
    if (!ctx || !this.musicWanted || this.isMuted) return

    // A backgrounded tab can leave the cursor far in the past — re-anchor
    // instead of scheduling hundreds of catch-up notes.
    if (this.nextStepTime < ctx.currentTime) this.nextStepTime = ctx.currentTime + 0.05

    const horizon = ctx.currentTime + LOOKAHEAD
    const dur = 60 / (BPM_MIN + (BPM_MAX - BPM_MIN) * this.intensity) / 4
    while (this.nextStepTime < horizon) {
      this.scheduleStep(this.step, this.nextStepTime, dur)
      this.nextStepTime += dur
      this.step = (this.step + 1) % TOTAL_STEPS
    }

    this.timer = window.setTimeout(this.tick, TIMER_MS)
  }

  private scheduleStep(step: number, t: number, stepDur: number): void {
    const bar = Math.floor(step / STEPS_PER_BAR) % PROGRESSION.length
    const inBar = step % STEPS_PER_BAR
    const chord = PROGRESSION[bar]

    if (inBar === 0) this.bassNote(hz(chord.root - 12), t, stepDur * 10)

    // The arp doubles up once the truck is really moving.
    const dense = this.intensity > 0.55
    if (inBar % 2 !== 0 && !dense) return

    const notes = [chord.root + 12, chord.root + 19, chord.root + 24, chord.root + chord.third + 24]
    // The 7th only joins in at speed — the loop gets tenser, not just faster.
    if (this.intensity > 0.45) notes.push(chord.root + 34)
    const note = notes[(dense ? inBar : inBar / 2) % notes.length]
    this.arpNote(hz(note), t, Math.max(stepDur * 1.6, 0.12))
  }

  private arpNote(freq: number, t: number, dur: number): void {
    const ctx = this.ctx
    const bus = this.musicBus
    if (!ctx || !bus) return

    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(freq, t)

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.Q.value = 7
    lp.frequency.setValueAtTime(620 + this.intensity * 3600, t)

    const g = ctx.createGain()
    envelope(g.gain, t, 0.008, dur, 0.5)

    osc.connect(lp).connect(g).connect(bus)
    osc.start(t)
    osc.stop(t + dur + 0.02)
    osc.onended = () => {
      osc.disconnect()
      lp.disconnect()
      g.disconnect()
    }
  }

  private bassNote(freq: number, t: number, dur: number): void {
    const ctx = this.ctx
    const bus = this.musicBus
    if (!ctx || !bus) return

    const osc = ctx.createOscillator()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(freq, t)

    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 260

    const g = ctx.createGain()
    envelope(g.gain, t, 0.02, dur, 0.85)

    osc.connect(lp).connect(g).connect(bus)
    osc.start(t)
    osc.stop(t + dur + 0.02)
    osc.onended = () => {
      osc.disconnect()
      lp.disconnect()
      g.disconnect()
    }
  }

  /* -------------------------------------------------------- voice plumbing */

  private newVoice(): Voice | null {
    const ctx = this.ctx
    const master = this.master
    if (!ctx || !master) return null

    const bus = ctx.createGain()
    bus.gain.value = 1
    bus.connect(master)

    const voice: Voice = { bus, sources: [], parts: [], dead: false }
    this.voices.push(voice)
    while (this.voices.length > MAX_VOICES) this.kill(this.voices[0])
    return voice.dead ? null : voice
  }

  private noiseSource(ctx: AudioContext): AudioBufferSourceNode | null {
    if (!this.noise) return null
    const src = ctx.createBufferSource()
    src.buffer = this.noise
    src.loop = true
    return src
  }

  /** Starts the voice's final source and wires the teardown to its `ended`. */
  private arm(v: Voice, src: AudioScheduledSourceNode, start: number, stop: number): void {
    v.sources.push(src)
    src.start(start)
    src.stop(stop)
    src.onended = () => this.kill(v)
  }

  private kill(v: Voice): void {
    if (v.dead) return
    v.dead = true
    for (const s of v.sources) {
      s.onended = null
      try {
        s.stop()
      } catch {
        /* already finished */
      }
      s.disconnect()
    }
    for (const n of v.parts) n.disconnect()
    v.bus.disconnect()
    const i = this.voices.indexOf(v)
    if (i >= 0) this.voices.splice(i, 1)
  }
}

/** Attack / exponential-decay envelope on a gain param. */
function envelope(param: AudioParam, t: number, attack: number, dur: number, peak: number): void {
  param.cancelScheduledValues(t)
  param.setValueAtTime(SILENCE, t)
  param.exponentialRampToValueAtTime(peak, t + attack)
  param.exponentialRampToValueAtTime(SILENCE, t + Math.max(dur, attack + 0.01))
}

/** One second of mono white noise, reused by every noise-based voice. */
function makeNoise(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate), ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1
  return buffer
}

export function createAudio(): AudioEngine {
  return new ProceduralAudio()
}
